import { useCallback, useMemo, useState } from 'react';
import { handleAxiosError } from 'core/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '@/util/axios';

import getEnrolledUsersInitialData from '../helpers/services/getEnrolledUsersInitialData';
import getEnrollmentsFilters from '../helpers/services/getEnrollmentsFilters';
import getAxiosConfig from '../helpers/services/getAxiosConfig';
import getEnrolledUsersTransform from '../helpers/transforms/getEnrolledUsersTransform';

export default ({ courseId, isModalVariant }) => {
  const queryClient = useQueryClient();

  const enrolledUsersQueryKey = useMemo(
    () => ['enrolledUsersQuery', courseId, isModalVariant],
    [courseId, isModalVariant],
  );
  const enrolledUsersQuery = useQuery(enrolledUsersQueryKey, () => {}, {
    initialData: {
      filters: { enrollment: {}, user: { query: '' } },
      pagination_params: { page: 1, per_page: 50 },
    },
    enabled: false,
  });

  // this has to be in state, or else updating the query in a child component won't
  // trigger a re-fetch until the parent component that uses this hook is re-rendered
  const [queryAsString, setQueryAsString] = useState(JSON.stringify(enrolledUsersQuery.data));

  const setEnrolledUsersQuery = (buildNewQuery) => {
    const newQuery = buildNewQuery(enrolledUsersQuery.data);
    queryClient.setQueryData(enrolledUsersQueryKey, newQuery);
    setQueryAsString(JSON.stringify(newQuery));
  };

  const resetEnrolledUsersQuery = useCallback(
    () => queryClient.resetQueries(enrolledUsersQueryKey),
    [enrolledUsersQueryKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const removeEnrolledUsersQuery = useCallback(
    () => queryClient.removeQueries(enrolledUsersQueryKey),
    [enrolledUsersQueryKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const enrolledUsersKey = useMemo(
    () => ['enrolledUsers', courseId, isModalVariant, queryAsString],
    [courseId, isModalVariant, queryAsString],
  );
  const enrolledUsers = useQuery(
    enrolledUsersKey,
    async ({ signal }) => {
      try {
        const { data } = await axios(
          getAxiosConfig('get-enrollments', {
            signal,
            courseId,
            isModalVariant,
            enrolledUsersQuery: enrolledUsersQuery.data,
          }),
        );
        return getEnrolledUsersTransform({ isModalVariant, data });
      } catch (error) {
        if (axios.isCancel(error)) return;
        handleAxiosError(error);
      }
    },
    { initialData: getEnrolledUsersInitialData() },
  );

  const enrollmentsGroupsKey = useMemo(() => ['enrollmentsGroups'], []);
  const enrollmentsGroups = useQuery(
    enrollmentsGroupsKey,
    async ({ signal }) => {
      try {
        const { data } = await axios(getAxiosConfig('get-groups', { signal }));
        return data;
      } catch (error) {
        if (axios.isCancel(error)) return;
        handleAxiosError(error);
      }
    },
    { initialData: [], enabled: isModalVariant },
  );

  const enrollmentsFilters = getEnrollmentsFilters({
    enrollmentsGroups,
    enrolledUsers,
    isModalVariant,
  });

  const enrollmentsSelectedUsersKey = useMemo(
    () => ['enrollmentsSelectedUsers', courseId, isModalVariant],
    [courseId, isModalVariant],
  );
  const enrollmentsSelectedUsers = useQuery(enrollmentsSelectedUsersKey, () => {}, {
    initialData: {
      count: 0,
      hasSelectedAllUsers: false,
      headerCheckboxStatus: 'unchecked',
      users: {},
    },
    enabled: false,
  });

  const setEnrollmentsSelectedUsers = (buildNewSelectedUsers) =>
    queryClient.setQueryData(
      enrollmentsSelectedUsersKey,
      buildNewSelectedUsers(enrollmentsSelectedUsers.data),
    );

  const resetEnrollmentsSelectedUsers = useCallback(
    () => queryClient.resetQueries(enrollmentsSelectedUsersKey),
    [enrollmentsSelectedUsersKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const removeEnrollmentsSelectedUsers = useCallback(
    () => queryClient.removeQueries(enrollmentsSelectedUsersKey),
    [enrollmentsSelectedUsersKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    enrollmentsGroups,
    enrollmentsGroupsKey,
    enrolledUsers,
    enrolledUsersKey,
    enrolledUsersQuery: enrolledUsersQuery.data,
    setEnrolledUsersQuery,
    resetEnrolledUsersQuery,
    removeEnrolledUsersQuery,
    enrollmentsFilters,
    enrollmentsSelectedUsers,
    setEnrollmentsSelectedUsers,
    resetEnrollmentsSelectedUsers,
    removeEnrollmentsSelectedUsers,
  };
};
